import React, { useEffect, useState } from "react";
import { Dialog } from "../../components/ui/Dialog";
import { fetchSchemesByBucketId } from "../../services/scheme";
import { Badge } from "../../components/ui/Badge";
import { Loader2, X, Calendar, DollarSign, Percent, Users } from "lucide-react";

interface BucketDetailsDialogProps {
    bucketId: string | null;
    isOpen: boolean;
    onClose: () => void;
    bucketName: string | null;
}

const BucketDetailsDialog: React.FC<BucketDetailsDialogProps> = ({
    bucketId,
    isOpen,
    bucketName,
    onClose,
}) => {
    const [loading, setLoading] = useState(false);
    const [schemes, setSchemes] = useState<any[]>([]);

    useEffect(() => {
        if (!isOpen || !bucketId) return;

        async function loadSchemes() {
            setLoading(true);
            try {
                const result = await fetchSchemesByBucketId(bucketId as string);
                setSchemes(result || []);
            } catch (err) {
                console.error("Error loading schemes:", err);
                setSchemes([]);
            } finally {
                setLoading(false);
            }
        }

        loadSchemes();
    }, [bucketId, isOpen]);

    return (
        <Dialog isOpen={isOpen} onClose={onClose} size="xl">
            {/* Header with X close button */}
            <div className="relative px-6 py-4 border-b border-gray-200">
                {/* Close button top-right */}
                <button
                    onClick={onClose}
                    className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition"
                >
                    <X size={20} className="text-gray-600" />
                </button>

                {/* Header */}
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Scheme Bucket Details
                </h3>

                {/* ID */}
                <div className="text-gray-700 font-medium mb-1">
                    ID: <span className="text-blue-600">{bucketId}</span>
                    <span className="ml-4">Name: <span className="text-blue-600">{bucketName}</span></span>
                </div>
            </div>


            {/* Loading */}
            {loading && (
                <div className="flex justify-center items-center py-16">
                    <Loader2 className="animate-spin w-8 h-8 text-blue-500" />
                </div>
            )}

            {/* Empty State */}
            {!loading && schemes.length === 0 && (
                <p className="text-center text-gray-500 py-16 text-lg">
                    No schemes found for this bucket.
                </p>
            )}

            {/* Schemes Grid */}
            {!loading && schemes.length > 0 && (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mt-6 px-6">
                    {schemes.map((scheme, index) => (
                        <div
                            key={index}
                            className="border rounded-2xl p-5 shadow-md hover:shadow-lg transition-transform transform hover:scale-[1.03] bg-white flex flex-col gap-4"
                        >
                            {/* Header */}
                            <div className="flex justify-between items-center">
                                <h3 className="font-bold text-lg text-gray-900">{scheme.name}</h3>
                                <Badge
                                    className={`px-2 py-1 text-sm font-medium ${scheme.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                                >
                                    {scheme.isActive ? "Active" : "Inactive"}
                                </Badge>
                            </div>

                            {/* Basic Info */}
                            <div className="flex flex-col gap-2">
                                <p className="text-sm text-gray-700 flex items-center gap-2">
                                    <Calendar size={16} className="text-blue-500" />
                                    <span className="font-medium">Tenure:</span> {scheme.grossTenure} months
                                </p>
                                <p className="text-sm text-gray-700 flex items-center gap-2">
                                    <DollarSign size={16} className="text-green-500" />
                                    <span className="font-medium">Advanced EMI:</span> {scheme.advancedEMI}
                                </p>
                                <p className="text-sm text-gray-700 flex items-center gap-2">
                                    <DollarSign size={16} className="text-yellow-500" />
                                    <span className="font-medium">Subvention Cost:</span> {scheme.subventionCost}
                                </p>
                                {scheme.interestBased && (
                                    <p className="text-sm text-gray-700 flex items-center gap-2">
                                        <Percent size={16} className="text-red-500" />
                                        <span className="font-medium">Annual Interest:</span> {scheme.annualInterest}%
                                    </p>
                                )}
                                <p className="text-sm text-gray-700 flex items-center gap-2">
                                    <Percent size={16} className="text-purple-500" />
                                    <span className="font-medium">Processing Rate:</span> {scheme.processingRate}%
                                </p>
                            </div>

                            {/* Category */}
                            {scheme.category?.length > 0 && (
                                <div>
                                    <p className="font-medium text-gray-700 mb-1">Category:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {scheme.category.map((cat: string, i: number) => {
                                            let categoryClass = "bg-gray-100 text-gray-800"; // default
                                            if (cat === "E2V") categoryClass = "bg-green-100 text-green-800";
                                            else if (cat === "CDR") categoryClass = "bg-pink-100 text-blue-800";

                                            return (
                                                <Badge key={i} className={`px-2 py-1 text-sm font-medium ${categoryClass}`}>
                                                    {cat}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}


                            {/* SubCategory */}
                            {scheme.subCategory?.length > 0 && (
                                <div>
                                    <p className="font-medium text-gray-700 mb-1">Sub Category:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {scheme.subCategory.map((sub: string, i: number) => (
                                            <Badge key={i} className="bg-purple-100 text-purple-800 px-2 py-1 text-sm font-medium">
                                                {sub}
                                            </Badge>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Lenders */}
                            {scheme.lender?.length > 0 && (
                                <div>
                                    <p className="font-medium text-gray-700 mb-1">Lender:</p>
                                    <div className="flex flex-wrap gap-2">
                                        {scheme.lender.map((ln: string, i: number) => {
                                            let lenderClass = "bg-blue-100 text-blue-700"; // default
                                            if (ln.toLowerCase() === "cashe") lenderClass = "bg-yellow-100 text-yellow-800";
                                            if (ln.toLowerCase() === "zype") lenderClass = "bg-[#000000] text-white";


                                            return (
                                                <Badge key={i} className={`px-2 py-1 text-sm font-medium ${lenderClass}`}>
                                                    {ln}
                                                </Badge>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </Dialog>
    );
};

export default BucketDetailsDialog;
